import I18n from 'I18n';
import getLang from 'I18n/utils/getLang';
import getLangLocale from 'I18n/utils/getLangLocale';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import Raven from 'raven-js';
import { memo, useCallback, useEffect, useState } from 'react';
import { createRpcClientV2 } from 'rpc-client-utils';
import apiClient from 'hub-http/clients/apiClient';
import { getUserUnsubscribeLinkSettingsRpc } from '../../../__generated__/chirp/com/hubspot/cursus/rpc/SendConfigurationRpc';
import { useReactNodeView } from '../utils/ReactNodeView';
import { LINK_TOKEN, nullUnsubscribeLinkType, unsubscribeLinkTypes } from './unsubscribeLink/UnsubscribeConstants';
import { jsx as _jsx } from "react/jsx-runtime";
const rpcClient = createRpcClientV2({
  hubHttpClient: apiClient
});
const UnsubscribeEdit = Loadable({
  loader: () => import('./unsubscribeLink/UnsubscribeEdit').then(module => module.default),
  LoadingComponent: () => null
});
const NULL_VIEW_MODEL = {
  loaded: false,
  locale: null,
  language: null,
  unsubscribeLinkType: nullUnsubscribeLinkType.NO_UNSUBSCRIBE_LINK
};

//Type Predicate bridges the two unsubscribeLinkType types:
//the User Preferences response (broad) and the UnsubscribeEdit Props (narrow)
const unsubscribeLinkTypeIsReadyToRender = link => {
  const isInTheNarrowEnum = Object.values(unsubscribeLinkTypes).includes(link);
  return isInTheNarrowEnum;
};

// Document how the "ready" state does not propagate nulls
// and narrows the unsubscribe link type
// language is still nullable, default provided in UnsubscribeEdit not required for render
const inReadyToRenderState = state => {
  const {
    locale,
    unsubscribeLinkType,
    loaded
  } = state;
  return loaded && !!locale && unsubscribeLinkTypeIsReadyToRender(unsubscribeLinkType);
};

// if not for memoization, UnsubscribeEdit would rerender a dozen times on page load and then on every keystroke
const MemoizedUnsubscribeEdit = /*#__PURE__*/memo(UnsubscribeEdit, (prevProps, nextProps) => {
  return prevProps.unsubscribeData.linkType === nextProps.unsubscribeData.linkType && prevProps.unsubscribeData.locale === nextProps.unsubscribeData.locale;
});
function getLinkTextFromType(linkType, {
  locale,
  link
}) {
  switch (linkType) {
    case unsubscribeLinkTypes.STOP_GETTING_MESSAGES:
      return I18n.text('hubspot-prosemirror.unsubscribeLink.STOP_GETTING_MESSAGES.TO_EMAIL', {
        locale,
        link
      });
    case unsubscribeLinkTypes.PREFER_LESS:
      return I18n.text('hubspot-prosemirror.unsubscribeLink.PREFER_LESS.TO_EMAIL', {
        locale,
        link
      });
    case unsubscribeLinkTypes.RELEVANT:
      return I18n.text('hubspot-prosemirror.unsubscribeLink.RELEVANT.TO_EMAIL', {
        locale,
        link
      });
    default:
      return '';
  }
}
const nodeAttributes = ({
  linkType,
  locale,
  language
}) => {
  const linkUrlText = I18n.text('hubspot-prosemirror.unsubscribeLink.link', {
    locale
  });
  const link = /*#__PURE__*/_jsx("a", {
    href: LINK_TOKEN,
    children: linkUrlText
  });
  const linkText = getLinkTextFromType(linkType, {
    locale,
    link
  });
  return {
    linkType,
    locale,
    language,
    linkText,
    linkUrlText
  };
};
export const nodeAttributesReady = attributes => {
  const {
    linkType,
    locale,
    language,
    linkText,
    linkUrlText
  } = attributes;
  return !!linkType && !!locale && !!language && typeof linkText === 'string' && !!linkUrlText;
};
export const nodeAttributesDifferFromViewModel = (attributes, viewModel) => {
  return attributes.linkType !== viewModel.unsubscribeLinkType && attributes.locale !== viewModel.locale && attributes.language !== viewModel.language;
};
export default function Unsubscribe() {
  const [viewModel, setViewModel] = useState(NULL_VIEW_MODEL);
  const context = useReactNodeView();
  const {
    view,
    getPos,
    node
  } = context;

  /*
    State management loop Effect:
    1) If there's state from node attributes, restore it
    2) If there's none,
      - fetch core parameters via CHIRP RPC
      - generate node attributes from core parameters
      - update the node attributes, so (1) will pick it up next rerender
  */
  useEffect(() => {
    const currentNodeAttrs = context.node ? context.node.attrs : {};
    if (nodeAttributesReady(currentNodeAttrs) && nodeAttributesDifferFromViewModel(currentNodeAttrs, viewModel)) {
      const readyViewModel = {
        loaded: true,
        locale: currentNodeAttrs.locale,
        language: currentNodeAttrs.language,
        unsubscribeLinkType: currentNodeAttrs.linkType
      };
      setViewModel(readyViewModel);
    } else if (!nodeAttributesReady(currentNodeAttrs) && !inReadyToRenderState(viewModel)) {
      rpcClient.call(getUserUnsubscribeLinkSettingsRpc, {}).then(({
        unsubscribeLinkType
      }) => {
        // if the user set NO_UNSUBSCRIBE_LINK, we don't need to do anything
        const locale = getLangLocale();
        const lang = getLang();
        const pos = typeof getPos === 'function' ? getPos() : undefined;
        if (!unsubscribeLinkType || unsubscribeLinkType === nullUnsubscribeLinkType.NO_UNSUBSCRIBE_LINK) {
          setViewModel(NULL_VIEW_MODEL);
        } else if (locale && lang && unsubscribeLinkTypeIsReadyToRender(unsubscribeLinkType) && view && pos) {
          const newAttributes = nodeAttributes({
            linkType: unsubscribeLinkType,
            locale,
            language: lang
          });
          const attributeUpdate = view.state.tr.setNodeMarkup(pos, undefined, newAttributes);
          view.dispatch(attributeUpdate);
        }
      }).catch(error => {
        Raven.captureException(error, {
          extra: error.cause
        });
        setViewModel(NULL_VIEW_MODEL);
      });
    }
  }, [view, getPos, context.node, viewModel, viewModel.loaded, viewModel.language, viewModel.locale, viewModel.unsubscribeLinkType]);
  const onConfirm = useCallback(unsubscribeData => {
    const attrs = !!node && node.attrs;
    const pos = typeof getPos === 'function' ? getPos() : undefined;
    if (view && pos && !!attrs) {
      const newAttributes = nodeAttributes({
        linkType: unsubscribeData.linkType,
        locale: unsubscribeData.locale,
        language: unsubscribeData.language
      });
      const attributeUpdate = view.state.tr.setNodeMarkup(pos, undefined, newAttributes);
      view.dispatch(attributeUpdate);
    }
  }, [node, view, getPos]);
  if (inReadyToRenderState(viewModel)) {
    const {
      locale,
      language,
      unsubscribeLinkType
    } = viewModel;
    const unsubscribeEditProps = {
      unsubscribeData: {
        open: false,
        locale,
        selectedLang: language,
        linkType: unsubscribeLinkType
      },
      onConfirm
    };
    return /*#__PURE__*/_jsx(MemoizedUnsubscribeEdit, Object.assign({}, unsubscribeEditProps));
  } else {
    return /*#__PURE__*/_jsx("div", {});
  }
}
export const unsubscribeViewUpdateFn = ({
  previousNode,
  node
}) => {
  if (previousNode.attrs.linkType !== node.attrs.linkType || previousNode.attrs.locale !== node.attrs.locale || previousNode.attrs.language !== node.attrs.language || previousNode.attrs.linkText !== node.attrs.linkText || previousNode.attrs.linkUrl !== node.attrs.linkUrl || previousNode.attrs.linkUrlText !== node.attrs.linkUrl) {
    return false;
  }
  return true;
};